// Ported from dev-scrips-pm-react src/billing/services/use-clinical-note.ts (PROD-1119 cross-module closure).
import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';

/**
 * Read-only encounter clinical data for the "View Clinical Note" side panel
 * (PROD-969c phase 3). Composes two encounter/clinical-stream reads (option B —
 * surfaced inside billing). Endpoints are under `/api/Patients`, NOT
 * `/api/Billing`. Wire shapes: `docs/CONVERSIONS/claim-969c/CONVERSION_NOTES.md`.
 *
 * These are READS ONLY — the panel never writes. Types are intentionally loose
 * (the clinical model is Samer's domain); we read just the fields the note view
 * renders and treat the rest as opaque.
 */

// ─── GetAllEncounterDetails (the encounter note: chief complaint + A&P + vitals)

export interface NoteMedication {
  identifier?: string | null;
  medicationCode?: { code?: string; display?: string };
  route?: { display?: string | null };
  frequency?: string | null;
  duration?: string | null;
  durationUnit?: string | null;
  dispense?: number | null;
  notes?: string | null;
  isDeleted?: boolean | null;
}

export interface NoteCondition {
  identifier?: string | null;
  isPrimary?: boolean | null;
  isDeleted?: boolean | null;
  conditionCode?: { code?: string; display?: string; system?: string | null };
  childMedicalCode?: {
    assessmentPlanMedication?: NoteMedication[] | null;
  } | null;
}

export interface NoteAssessment {
  identifier?: string;
  notes?: string | null;
  notePlainText?: string | null;
  assessmentPlanCondition?: NoteCondition[] | null;
}

export interface NoteComplaint {
  type?: string;
  coding?: { code?: string; display?: string; system?: string };
  complaintCategory?: string;
}

export interface NoteVital {
  loincCode?: string;
  quantityValue?: string | null;
  quantityUnit?: string | null;
  vitalNormalStatus?: string | null;
  effectiveDateTime?: string | null;
}

export interface NoteVitalsCapture {
  createdDate?: string | null;
  createdByName?: string | null;
  observationsVitals?: NoteVital[] | null;
}

export interface EncounterClinicalDetails {
  enconterDetails?: Array<{
    identifier?: string;
    status?: string;
    reasonForVisit?: string | null;
    startDate?: string | null;
    locationName?: string | null;
    performerDetails?: { name?: string } | null;
  }>;
  chiefComplaints?: { complaints?: NoteComplaint[] | null } | null;
  assessmentResponse?: NoteAssessment[] | null;
  vitalsResponse?: NoteVitalsCapture[] | null;
  /** Presence/length only — drive the "No ROS / Physical Exam yet" empty states. */
  rosDetails?: unknown[] | null;
  pEDetails?: unknown[] | null;
}

/** `GET /api/Patients/GetAllEncounterDetails?Id={encounterId}&patientId={patientId}`. */
export function useEncounterClinicalDetails(params: {
  encounterId?: string;
  patientId?: string;
  enabled?: boolean;
}) {
  const { encounterId, patientId, enabled = true } = params;
  return useQuery({
    queryKey: ['encounter-clinical-details', encounterId, patientId] as const,
    queryFn: ({ signal }) =>
      get<EncounterClinicalDetails>(
        `/api/Patients/GetAllEncounterDetails?Id=${encounterId}&patientId=${patientId}`,
        { signal },
      ),
    enabled: enabled && Boolean(encounterId) && Boolean(patientId),
  });
}

// ─── GetPatientSummary (supporting context: allergies + active problems) ──────

export interface PatientSummaryAllergy {
  identifier?: string;
  code?: { display?: string };
  criticality?: { display?: string };
  category?: { display?: string } | null;
  isDeleted?: boolean | null;
}

export interface PatientSummaryCondition {
  identifier?: string;
  code?: { code?: string; display?: string };
  onsetDisplayDateTime?: string | null;
  isDeleted?: boolean | null;
}

export interface PatientSummaryProcedure {
  identifier?: string;
  code?: { code?: string; displayName?: string; display?: string };
  isDeleted?: boolean | null;
}

export interface PatientSummaryImmunization {
  identifier?: string;
  vaccine?: { code?: string; display?: string };
  isDeleted?: boolean | null;
}

export interface PatientSummaryFamilyHistory {
  identifier?: string;
  code?: Array<{ code?: string; display?: string }> | null;
  isDeleted?: boolean | null;
}

export interface PatientClinicalSummary {
  allergies?: PatientSummaryAllergy[] | null;
  activeConditions?: PatientSummaryCondition[] | null;
  procedures?: PatientSummaryProcedure[] | null;
  immunization?: PatientSummaryImmunization[] | null;
  familyHistory?: PatientSummaryFamilyHistory[] | null;
}

/** `GET /api/Patients/GetPatientSummary?PatientId={patientId}`. */
export function usePatientClinicalSummary(params: {
  patientId?: string;
  enabled?: boolean;
}) {
  const { patientId, enabled = true } = params;
  return useQuery({
    queryKey: ['patient-clinical-summary', patientId] as const,
    queryFn: ({ signal }) =>
      get<PatientClinicalSummary>(
        `/api/Patients/GetPatientSummary?PatientId=${patientId}`,
        { signal },
      ),
    enabled: enabled && Boolean(patientId),
  });
}

// ─── GetEncounterVitalsHistory (current-encounter vitals) ─────────────────────

interface VitalsHistoryResponse {
  currentEncounterVitals?: Array<
    NoteVitalsCapture | { observationsVitals?: NoteVital[] }
  > | null;
}

/**
 * Current-encounter vitals for the note. `GET
 * /api/Patients/GetEncounterVitalsHistory?patientId={}&encounterId={}`. The
 * panel only uses `currentEncounterVitals` (this visit) — flattened to a flat
 * `NoteVital[]`; usually empty at claim time (→ all "—" in the list).
 */
export function useEncounterVitals(params: {
  encounterId?: string;
  patientId?: string;
  enabled?: boolean;
}) {
  const { encounterId, patientId, enabled = true } = params;
  return useQuery({
    queryKey: ['encounter-vitals', encounterId, patientId] as const,
    queryFn: async ({ signal }) => {
      const data = await get<VitalsHistoryResponse>(
        `/api/Patients/GetEncounterVitalsHistory?patientId=${patientId}&encounterId=${encounterId}`,
        { signal },
      );
      const captures = data?.currentEncounterVitals ?? [];
      return captures.flatMap((c) =>
        Array.isArray((c as NoteVitalsCapture).observationsVitals)
          ? ((c as NoteVitalsCapture).observationsVitals ?? [])
          : [],
      );
    },
    enabled: enabled && Boolean(encounterId) && Boolean(patientId),
  });
}
